import React from 'react';
import { 
  Box, 
  Typography, 
  Breadcrumbs, 
  Link, 
  Paper, 
  Button, 
  Divider, 
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  AccountTree as AccountTreeIcon,
  People as PeopleIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { zoullooColors } from '../../theme';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompany, useCompanyAcceptedTalents } from '../../hooks/useCompany';
import { useEnumTranslation } from '../../utils/translateEnum';
import { useEntityTranslations } from '../../hooks/useTranslations';
import { LanguageCode } from '../../models/enums/LanguageCode';
import { useLanguage } from '../../i18n/LanguageContext';
import TranslatableFieldWrapper from '../../components/translation/TranslatableFieldWrapper';

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const companyId = parseInt(id || '0');
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Get translation functions for enum values
  const { translateCompanySize, translateCompanyType } = useEnumTranslation();
  
  const { data: company, isLoading, error } = useCompany(companyId);
  const { data: acceptedTalents, isLoading: isLoadingTalents } = useCompanyAcceptedTalents(companyId);

  // Language switching for translatable fields — defaults to user's preferred language
  const { currentLanguage } = useLanguage();
  const [viewingLanguage, setViewingLanguage] = React.useState<LanguageCode>(currentLanguage);

  // Sync if global language changes
  React.useEffect(() => {
    setViewingLanguage(currentLanguage);
  }, [currentLanguage]);
  const { data: translations } = useEntityTranslations(
    'Company',
    companyId,
    viewingLanguage.toLowerCase()
  );

  // Get field value in the currently selected language
  const getTranslatedField = (fieldName: string, frValue?: string | null): string => {
    if (viewingLanguage === LanguageCode.Fr) {
      return frValue || '-';
    }
    const pascalField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    return translations?.[pascalField] || frValue || '-';
  };

  const handleEdit = () => {
    navigate(`/companies/${companyId}/edit`);
  };
  
  const handleNavigateToList = () => {
    navigate('/companies');
  };
  
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !company) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t('company.errorLoadingCompany')}: {error?.message || t('company.notFound')}
        </Typography>
      </Box>
    );
  }
  
  // Find the headquarters address if it exists
  const headquartersAddress = company.addresses?.find(
    addr => addr.addressType.code === 'HEADQUARTERS' && addr.isDefault
  );
  
  return (
    <Box p={3}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h2">{company.legalName}</Typography>
      <Button
        variant="contained"
        startIcon={<EditIcon />}
        onClick={handleEdit}
        sx={{
          backgroundColor: zoullooColors.secondary.main,
          '&:hover': {
            backgroundColor: zoullooColors.secondary.dark,
          },
        }}
      >
        {t('common.edit')}
      </Button>
    </Box>
      
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToList}
          sx={{ cursor: 'pointer' }}
        >
          {t('company.companyList')}
        </Link>
        <Typography color="text.primary">{company.legalName}</Typography>
      </Breadcrumbs>
      
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon sx={{ mr: 1, color: zoullooColors.secondary.dark }} />
              <Typography variant="h4" color={zoullooColors.secondary.dark}>
                {t('company.companyInformation')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('company.legalName')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {company.legalName}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('company.tradingName')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {company.tradingName || '-'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('company.industry')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {company.sector ? t(`Sectors.${company.sector.label}`) : '-'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('company.size')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {company.companySize ? translateCompanySize(company.companySize) : '-'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('company.type')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {company.companyType ? translateCompanyType(company.companyType) : '-'}
                </Typography>
              </Grid>
              
              {company.website && (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.website')}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <LanguageIcon sx={{ mr: 1, fontSize: 'small', color: zoullooColors.primary.main }} />
                    <Link href={company.website} target="_blank" rel="noopener">
                      {company.website}
                    </Link>
                  </Box>
                </Grid>
              )}

              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('company.group')}
                </Typography>
                {company.companyGroup ? (
                  <Box display="flex" alignItems="center">
                    <AccountTreeIcon sx={{ mr: 1, fontSize: 'small', color: zoullooColors.primary.main }} />
                    <Link
                      onClick={() => navigate(`/company-groups/${company.companyGroup!.id}`)}
                      sx={{ cursor: 'pointer' }}
                      underline="hover"
                    >
                      {company.companyGroup.name}
                    </Link>
                  </Box>
                ) : (
                  <Typography variant="body1" gutterBottom>
                    -
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Address Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <LocationOnIcon sx={{ mr: 1, color: zoullooColors.secondary.dark }} />
              <Typography variant="h4" color={zoullooColors.secondary.dark}>
                {t('company.headquartersAddress')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {headquartersAddress ? (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.fullAddress')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.fullAddress}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.street')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.street || '-'}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.streetNumber')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.streetNumber || '-'}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.postalCode')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.postalCode || '-'}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.city')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.city}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.region')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.region || '-'}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.country')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {headquartersAddress.address.country}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body1" color="text.secondary">
                {t('company.noAddressFound')}
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Additional information (Translatable fields) */}
        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <Typography variant="h4" color={zoullooColors.secondary.dark}>
                  {t('company.additionalInformation')}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
              <Grid size={12}>
                <TranslatableFieldWrapper
                  label={t('company.description')}
                  editingLanguage={viewingLanguage}
                  onLanguageChange={setViewingLanguage}
                >
                  <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                    {getTranslatedField('description', company.description)}
                  </Typography>
                </TranslatableFieldWrapper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TranslatableFieldWrapper
                  label={t('company.market')}
                  editingLanguage={viewingLanguage}
                  onLanguageChange={setViewingLanguage}
                >
                  <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                    {getTranslatedField('market', company.market)}
                  </Typography>
                </TranslatableFieldWrapper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TranslatableFieldWrapper
                  label={t('company.geographicPresence')}
                  editingLanguage={viewingLanguage}
                  onLanguageChange={setViewingLanguage}
                >
                  <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                    {getTranslatedField('geographicPresence', company.geographicPresence)}
                  </Typography>
                </TranslatableFieldWrapper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Accepted Talents */}
        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PeopleIcon sx={{ mr: 1, color: zoullooColors.secondary.dark }} />
              <Typography variant="h4" color={zoullooColors.secondary.dark}>
                {t('company.acceptedTalents')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {isLoadingTalents ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={30} />
              </Box>
            ) : !acceptedTalents || acceptedTalents.length === 0 ? (
              <Typography variant="body1" color="text.secondary">
                {t('company.noAcceptedTalents')}
              </Typography>
            ) : (
              <Box>
                {acceptedTalents.map((talent) => (
                  <Box key={talent.freelancerProfileId} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <PeopleIcon sx={{ mr: 1, fontSize: 'small', color: zoullooColors.primary.main }} />
                      <Link
                        onClick={() => navigate(`/applications/talent/${talent.freelancerProfileKey}`)}
                        sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                        underline="hover"
                      >
                        {talent.freelancerName}
                      </Link>
                      {talent.profileTitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          — {talent.profileTitle}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('company.acceptedOnProjects')}
                      </Typography>
                      {talent.projects.map((project) => (
                        <Box key={project.projectId} display="flex" alignItems="center" ml={1}>
                          <WorkIcon sx={{ mr: 0.5, fontSize: 'small', color: zoullooColors.secondary.main }} />
                          <Link
                            onClick={() => navigate(`/projects/${project.projectId}`)}
                            sx={{ cursor: 'pointer' }}
                            underline="hover"
                          >
                            {project.projectName}
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyDetails;